/**
 * Report Library Page
 * Lists, searches, and manages previously generated MMM HTML reports.
 * Supports named report templates for the "7-Report / Media Assistant" scope.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Download, Trash2,
  Eye,
  ArrowLeft,
  FileCode,
  Inbox,
  LayoutGrid,
} from 'lucide-react';
import {
  getSavedReports,
  openReportInNewTab,
} from '../services/mmmReportService';
import {
  listReports,
  fetchReportHtml,
  deleteReport,
  uploadLocalReports,
  type ReportSummary,
} from '@/services/reportService';
import { downloadReportHtml } from "@/services/downloadReport";
import { YDMPageHero } from '../components/brand';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { REPORT_TEMPLATES } from '../config/reportTemplates';
import { logError } from '../services/logger';

/**
 * The first 200 characters of a report's summary with its HTML stripped, or an
 * empty string when the report has none.
 *
 * The ellipsis is appended here rather than in the markup so that "there is no
 * summary" and "the summary is long" cannot render the same way.
 */
const summaryPreview = (summary: string | undefined): string => {
  const text = (summary ?? '').replace(/<[^>]+>/g, '').trim();
  if (!text) return '';
  return text.length > 200 ? `${text.slice(0, 200)}...` : text;
};

const ReportLibrary: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const [allReports, setAllReports] = useState<ReportSummary[]>([]);
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [activeTemplate, setActiveTemplate] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  // Load this workspace's reports. Keyed on the workspace so switching client
  // reloads the library instead of leaving the previous one's reports on screen.
  useEffect(() => {
    if (!workspace) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Anything this browser still holds from before the shelf was shared
        // goes up first, so the list below includes it rather than appearing to
        // have lost it. Failures here are not fatal: the local copy is intact,
        // and the next visit tries again.
        try {
          const local = await getSavedReports(workspace.id);
          await uploadLocalReports(workspace.id, local, t);
        } catch (err) {
          logError('ReportLibrary', 'Could not migrate local reports', err);
        }

        const result = await listReports(workspace.id, t);
        if (cancelled) return;
        if (result.success && result.data) {
          setAllReports(result.data);
          setLoadError(null);
        } else {
          // An empty shelf and a failed read look identical and mean opposite
          // things: one says nobody has made a report, the other says we could
          // not look. The audit trail learned this the same way.
          setAllReports([]);
          setLoadError(result.error ?? t('reports.loadFailed'));
        }
      } catch (err) {
        logError('ReportLibrary', 'Could not read saved reports', err);
        if (!cancelled) { setAllReports([]); setLoadError(t('reports.loadFailed')); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workspace?.id]);

  // Filter reports client-side (allReports is already loaded)
  useEffect(() => {
    let result = allReports;
    if (activeTemplate !== 'all') {
      result = result.filter((r) => {
        const template = REPORT_TEMPLATES[activeTemplate];
        if (!template) return true;
        return (
          r.title.includes(template.labelTh) ||
          r.scenario === activeTemplate ||
          r.title.includes(activeTemplate)
        );
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          (r.title ?? '').toLowerCase().includes(q) ||
          (r.scenario ?? '').toLowerCase().includes(q) ||
          (r.summary ?? '').toLowerCase().includes(q)
      );
    }
    setReports(result);
  }, [allReports, activeTemplate, searchQuery]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    if (!workspace) return;
    const result = await deleteReport(id, t);
    if (!result.success) {
      setLoadError(result.error ?? t('reports.deleteFailed'));
      setConfirmDelete(null);
      return;
    }
    // Removed from the list rather than refetched: the server has already
    // agreed, and a round trip only delays the thing the reader just asked for.
    setAllReports((prev) => prev.filter((r) => r.id !== id));
    setConfirmDelete(null);
  }, [workspace?.id, t]);

  /**
   * The document is fetched here, not with the list. Twenty cards do not need
   * twenty complete reports; one open does.
   */
  const handleOpen = useCallback(async (report: ReportSummary) => {
    const result = await fetchReportHtml(report.id, t);
    if (!result.success || !result.data) {
      setLoadError(result.error ?? t('reports.openFailed'));
      return;
    }
    openReportInNewTab(result.data);
  }, [t]);

  /**
   * The only way a report reaches anyone but the person who made it.
   * The library lives in this browser, so without a file there is nothing to
   * attach to an email and nothing to hand the client the report is about.
   */
  const handleDownload = useCallback(async (report: ReportSummary) => {
    const result = await fetchReportHtml(report.id, t);
    if (!result.success || !result.data) {
      setLoadError(result.error ?? t('reports.openFailed'));
      return;
    }
    downloadReportHtml(result.data, report.title, report.createdAt);
  }, [t]);

  const formatDate = (iso: string): string => {
    const d = new Date(iso);
    // A report saved without a date is readable now that the store no longer
    // hides undated rows behind the createdAt index. Date('') is Invalid Date,
    // and "Invalid Date" on a card reads as a broken app rather than as a
    // report whose age nobody recorded.
    if (Number.isNaN(d.getTime())) return t('reports.unknownDate');
    // From the language itself. This used to sniff the subtitle for the word
    // "browser", which tied the date format to a sentence that has since changed.
    const locale = (i18n?.language ?? 'th').startsWith('th') ? 'th-TH' : 'en-US';
    return d.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * `report.scenario` holds the TEMPLATE key — both generators store
   * `template.key` — but this badge still switched on the old
   * option1/option2/baseline scenario values, so every card fell through to
   * "Max Revenue", including performance reports for which the label means
   * nothing. The template chips above read the same field correctly.
   */
  const scenarioBadge = (scenario: string): { label: string; color: string } => {
    const template = REPORT_TEMPLATES[scenario as keyof typeof REPORT_TEMPLATES];
    if (template) return { label: template.labelTh, color: template.badgeColor };
    return { label: scenario || t('misc.reportFallbackLabel'), color: 'bg-neutral-100 text-ink-700' };
  };

  return (
    <div>
      <div className="space-y-6">
        <YDMPageHero
          eyebrow={t('reports.eyebrow')}
          headline={t('reports.headline')}
          subtitle={t('reports.subtitle')}
        />

        {/* Back button */}
        <button
          onClick={() => navigate('/budget')}
          className="flex items-center gap-2 text-sm text-neutral-700 hover:text-ink-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('reports.backToMMM')}</span>
        </button>

        {/* Template filter chips */}
        {/* Ten filter chips and a storage note over an empty library: controls
            for narrowing nothing, and a rule about a cap nothing is near. They
            appear once there is a report to filter. */}
        {allReports.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-neutral-700 mr-1">
            <LayoutGrid className="w-3.5 h-3.5" />
            <span>{t('reports.reportType')}</span>
          </div>
          <button
            onClick={() => setActiveTemplate('all')}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              activeTemplate === 'all'
                ? 'bg-ink-800 text-white'
                : 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50'
            }`}
          >
            {t('reports.all')}
          </button>
          {Object.values(REPORT_TEMPLATES).map((template) => (
            <button
              key={template.key}
              onClick={() => setActiveTemplate(template.key)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                activeTemplate === template.key
                  ? 'bg-ink-800 text-white'
                  : 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              {/* labelTh stays the DATA — stored report titles embed it and the
                  filter below matches on it — so only the chip's display is
                  translated. A dynamic key cannot be checked by
                  localeKeyIntegrity, hence the Thai default rather than a key
                  on screen if one is ever missing. */}
              {t(`reports.template.${template.key}`, { defaultValue: template.labelTh })}
            </button>
          ))}
        </div>
        )}

        {/* Whose shelf this is. It is the workspace's, on the server, and
            every member reads it — the old note said "kept in this browser, 50
            max", which described the local IndexedDB copy, not this list. */}
        {allReports.length > 0 && (
          <p className="text-xs text-neutral-700">
            {t('reports.storageNote', { count: allReports.length })}
          </p>
        )}

        {/* Search bar — same rule as the filters above: gated on allReports,
            not on the filtered list, so it stays reachable when a filter or a
            query matches nothing and the reader needs it to get back. */}
        {allReports.length > 0 && (
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-700" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder={t('reports.searchPlaceholder')}
            className="w-full pl-12 pr-4 py-3 border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ink-900 focus:border-transparent"
          />
        </div>
        )}

        {/* A failed read must never render as an empty shelf: one says nobody
            has made a report, the other says we could not look. */}
        {loadError && (
          <div role="alert" className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-800">
            {loadError}
          </div>
        )}

        {/* Report list */}
        {loading ? (
          <div className="text-center py-20 bg-white rounded-xl border border-neutral-200">
            <div className="inline-block w-8 h-8 border-3 border-neutral-200 border-t-ink-800 rounded-full animate-spin mb-4" />
            {/* No `|| 'Loading reports...'` here: a missing key makes t() return
                the KEY, which is truthy, so that fallback could never fire — it
                read as a safety net while the screen showed "reports.loading". */}
            <p className="text-sm text-neutral-700">{t('reports.loading')}</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-neutral-200">
            <Inbox className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
            <p className="text-neutral-700 font-medium">{t('reports.emptyTitle')}</p>
            <p className="text-sm text-neutral-700 mt-1">
              {t('reports.emptyMessage')}
            </p>
            {/* No "create a report" button: /mmm made these reports, and /budget, which replaced it, has no export
                yet (D5 ruling T16a; phase 2). */}
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => {
              const badge = scenarioBadge(report.scenario);
              return (
                <div
                  key={report.id}
                  className="bg-white rounded-xl border border-neutral-200 p-4 hover:shadow-md transition-shadow group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileCode className="w-5 h-5 text-emerald-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-ink-900 truncate">{report.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.color}`}>
                            {badge.label}
                          </span>
                          <span className="text-xs text-neutral-700">{formatDate(report.createdAt)}</span>
                        </div>
                        {/* AI summary preview (strip HTML tags). Rendered only
                            when there is one: a report saved before summaries
                            existed used to print a bare "..." — an ellipsis that
                            promises text and shows none. */}
                        {summaryPreview(report.summary) && (
                          <p className="text-sm text-neutral-700 mt-2 line-clamp-2">
                            {summaryPreview(report.summary)}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => void handleOpen(report)}
                        className="p-2 text-neutral-700 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                        title={t('reports.viewReport')}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => void handleDownload(report)}
                        className="p-2 text-neutral-700 hover:text-ink-900 hover:bg-neutral-100 rounded-lg transition-colors"
                        title={t('reports.downloadReport')}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {confirmDelete === report.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(report.id)}
                            className="px-2 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700"
                          >
                            {t('reports.confirmDelete')}
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            className="px-2 py-1 text-xs bg-neutral-200 text-ink-700 rounded-md hover:bg-neutral-300"
                          >
                            {t('reports.cancelDelete')}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(report.id)}
                          className="p-2 text-neutral-700 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          title={t('reports.deleteReport')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportLibrary;
